import { cn } from '@repo/tailwind-config/tailwind.functions'
import Button from '@repo/ui/button'
import Loader from '@repo/ui/loader'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getEndOfJourneyScanItemEnrollmentCandidates } from '../../../api/end-of-journey-scans/get-end-of-journey-scan-item-enrollment-candidates'
import { getEndOfJourneyScanItemManualForm } from '../../../api/end-of-journey-scans/get-end-of-journey-scan-item-manual-form'
import { getEnrollmentEndOfJourneyQuestionnaire } from '../../../api/end-of-journey-scans/get-enrollment-end-of-journey-questionnaire'
import useTranslate from '../../../lib/hooks/use-translate'
import { endOfJourneyScanUncertainTypeMapping } from '../../../lib/types/enums/end-of-stay-template/end-of-journey-scan.enum'
import {
  EndOfJourneyAnswerInput,
  EndOfJourneyQuestionnaire,
  EndOfJourneyScanBlankQuestion,
  EndOfJourneyScanEnrollmentCandidate,
  EndOfJourneyScanUncertainQuestion
} from '../../../lib/types/models/end-of-journey-scan/end-of-journey-scan'
import { extractAllErrorMessages } from '../../../lib/utils/functions/misc.functions'
import { ReviewLoadError } from '../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'
import { uncertainReason } from './end-of-journey-scan.utils'

type SelectedEnrollment = EndOfJourneyScanEnrollmentCandidate

type Props = {
  // Correction of a saved questionnaire: the youth is fixed
  enrollment?: SelectedEnrollment
  // Manual entry of this scanned questionnaire: blank form of the stay's template (backend's)
  itemId?: number
  // Manual entry: the youth already known from the scan, can be changed
  initialEnrollment?: SelectedEnrollment
  note?: string
  // Correction of a scanned questionnaire: the questions read uncertain, and the unanswered ones
  uncertainQuestions?: EndOfJourneyScanUncertainQuestion[]
  blankQuestions?: EndOfJourneyScanBlankQuestion[]
  loading?: boolean
  onCancel: () => void
  onSubmit: (input: { enrollmentId: number; answers: EndOfJourneyAnswerInput[] }) => void
}

// Blank answer: the question was not answered on the paper
const NOT_ANSWERED = 0

const EndOfJourneyQuestionnaireForm = ({
  enrollment,
  itemId,
  initialEnrollment,
  note,
  uncertainQuestions,
  blankQuestions,
  loading,
  onCancel,
  onSubmit
}: Props) => {
  const { t } = useTranslate()
  const [selected, setSelected] = useState<SelectedEnrollment | undefined>(
    enrollment ?? initialEnrollment
  )
  const [text, setText] = useState('')
  const [search, setSearch] = useState('')
  const [ratings, setRatings] = useState<Record<number, number>>({})

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(text), 500)
    return () => clearTimeout(timeout)
  }, [text])

  // Manual entry: the youths the backend allows for this scanned questionnaire
  // The backend's refusal (e.g. questionnaire not failed) is shown, never an empty picker
  const {
    data: candidates,
    isFetching: isFetchingCandidates,
    error: candidatesError
  } = useQuery({
    queryKey: ['end-of-journey-scan-enrollment-candidates', itemId, search],
    queryFn: () => getEndOfJourneyScanItemEnrollmentCandidates(itemId!, search),
    enabled: !enrollment && !!itemId
  })

  // Manual entry: the stay template's questions, blank; correction: the saved answers
  // The backend's refusal (e.g. youth outside the questionnaire's stay) is shown, never an empty form
  const {
    data: questionnaire,
    isFetching,
    error
  } = useQuery({
    queryKey: itemId
      ? ['end-of-journey-scan-item-manual-form', itemId, selected?.id]
      : ['end-of-journey-questionnaire', selected?.id],
    queryFn: async (): Promise<EndOfJourneyQuestionnaire & { alreadyReceived?: boolean }> => {
      if (!itemId) return getEnrollmentEndOfJourneyQuestionnaire(selected!.id)
      const form = await getEndOfJourneyScanItemManualForm(itemId, selected!.id)
      return {
        received: form.alreadyReceived,
        receivedAt: null,
        source: null,
        alreadyReceived: form.alreadyReceived,
        template: form.template,
        questions: form.questions.map((question) => ({ ...question, rating: null }))
      }
    },
    enabled: !!selected
  })

  // The form's refusal is not repeated when it is the one already shown under the youth picker
  // (e.g. questionnaire not failed: both requests are refused for the same reason)
  const shownCandidatesError = isFetchingCandidates ? null : candidatesError
  const errorShownAbove =
    !!error &&
    !!shownCandidatesError &&
    extractAllErrorMessages(error).join(' ') ===
      extractAllErrorMessages(shownCandidatesError).join(' ')

  useEffect(() => {
    setRatings(
      Object.fromEntries(
        (questionnaire?.questions ?? []).map((question) => [
          question.id,
          question.rating ?? NOT_ANSWERED
        ])
      )
    )
  }, [questionnaire])

  const uncertainById = new Map(
    (uncertainQuestions ?? []).map((question) => [question.questionId, question])
  )
  const blankIds = new Set((blankQuestions ?? []).map((question) => question.questionId))

  const submit = () => {
    if (!selected || !questionnaire) return
    onSubmit({
      enrollmentId: selected.id,
      answers: questionnaire.questions.map((question) => ({
        questionId: question.id,
        rating: ratings[question.id] === NOT_ANSWERED ? null : ratings[question.id]
      }))
    })
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {note && <p className="text-sm text-orange">{note}</p>}

      {enrollment ? (
        <p className="font-semibold">{enrollment.label}</p>
      ) : (
        <SelectInput<number>
          label={t('end.of.journey.questionnaire.youth')}
          searchable
          selectedOption={selected ? { value: selected.id, label: selected.label } : undefined}
          options={candidates?.map((candidate) => ({
            value: candidate.id,
            label: candidate.label
          }))}
          onTextChange={setText}
          onChange={(value) => {
            const candidate = candidates?.find((candidate) => candidate.id === value)
            if (candidate) setSelected(candidate)
          }}
        />
      )}

      {shownCandidatesError && <ReviewLoadError error={shownCandidatesError} />}

      {isFetching && <Loader />}

      {error && !isFetching && !errorShownAbove && <ReviewLoadError error={error} />}

      {questionnaire?.alreadyReceived && (
        <p className="text-sm text-orange">{t('end.of.journey.questionnaire.already.received')}</p>
      )}

      {questionnaire && !questionnaire.template && (
        <p className="text-sm text-red">{t('end.of.journey.questionnaire.no.template')}</p>
      )}

      {questionnaire?.template && (
        <div className="flex flex-col gap-3">
          {questionnaire.questions.map((question) => {
            const uncertain = uncertainById.get(question.id)
            const blank = !uncertain && blankIds.has(question.id)
            return (
              <div
                key={question.id}
                className={cn('flex flex-col gap-1', {
                  'rounded-lg border border-orange bg-orange-light p-2': !!uncertain
                })}
              >
                <SelectInput<number>
                  label={question.question}
                  selectedOption={{
                    value: ratings[question.id] ?? NOT_ANSWERED,
                    label:
                      (ratings[question.id] ?? NOT_ANSWERED) === NOT_ANSWERED
                        ? t('end.of.journey.questionnaire.not.answered')
                        : t('end.of.journey.questionnaire.rating', {
                            rating: ratings[question.id],
                            scaleMax: question.scaleMax
                          })
                  }}
                  options={[
                    ...Array.from({ length: question.scaleMax }, (_, index) => {
                      const rating = question.scaleMax - index
                      return {
                        value: rating,
                        label: t('end.of.journey.questionnaire.rating', {
                          rating,
                          scaleMax: question.scaleMax
                        })
                      }
                    }),
                    { value: NOT_ANSWERED, label: t('end.of.journey.questionnaire.not.answered') }
                  ]}
                  onChange={(rating) => setRatings((prev) => ({ ...prev, [question.id]: rating }))}
                />
                {uncertain && (
                  <span className="text-sm text-orange">
                    {uncertainReason(
                      t(endOfJourneyScanUncertainTypeMapping[uncertain.type]),
                      uncertain.options
                    )}
                  </span>
                )}
                {blank && (
                  <span className="text-sm text-grey-lighter">
                    {t('end.of.journey.questionnaire.blank.on.copy')}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          label={t('end.of.journey.questionnaire.cancel')}
          onClick={onCancel}
        />
        <Button
          label={t('end.of.journey.questionnaire.save')}
          loading={loading}
          disabled={!questionnaire?.template || isFetching}
          onClick={submit}
        />
      </div>
    </div>
  )
}

export default EndOfJourneyQuestionnaireForm
